import { BookOpen, ClipboardCheck, MessageCircle, Repeat, UserPlus, Users } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { JSX } from 'react';

import { Card, CardContent } from '@/components/ui/card';

/**
 * The two honest feature lists shared by the "start alone" panel and the
 * "join a pondok" card (ADR-031).
 *
 * Both describe only what this platform has built and running today. What
 * a solo learner gets and what a pondok adds are listed separately and
 * honestly: there is no teacher in a personal space, so evaluation and
 * murajaah scheduling are named as things joining brings, not things
 * quietly implied to already work alone.
 *
 * They live here rather than beside either caller because the join card
 * had to leave `NoMembershipPanel` - that panel renders only for an
 * account belonging to nothing, which made the whole explanation of how
 * to join vanish the moment someone started alone.
 */

interface FeaturePreview {
  readonly icon: LucideIcon;
  readonly title: string;
  readonly description: string;
}

export const SOLO_FEATURES: readonly FeaturePreview[] = [
  {
    icon: BookOpen,
    title: 'Target hafalan sendiri',
    description: 'Tentukan sendiri surah dan rentang ayat yang ingin Anda hafal.',
  },
  {
    icon: ClipboardCheck,
    title: 'Catatan setoran',
    description: 'Rekam sendiri hafalan yang sudah Anda setorkan, ayat demi ayat.',
  },
];

export const COMMUNITY_FEATURES: readonly FeaturePreview[] = [
  {
    icon: Users,
    title: 'Ustadz pembimbing',
    description: 'Ada yang menguji hafalan Anda dan menilainya - tajwid, kelancaran, ketepatan.',
  },
  {
    icon: Repeat,
    title: 'Murajaah terjadwal',
    description: 'Antrean murajaah yang memprioritaskan hafalan yang paling perlu diulang.',
  },
  {
    icon: MessageCircle,
    title: 'Ruang komunikasi',
    description: 'Percakapan langsung antara santri dan ustadz pembimbingnya.',
  },
  {
    icon: UserPlus,
    title: 'Materi dari ustadz',
    description: 'Bahan dan arahan hafalan yang diberikan khusus di ruang pondok Anda.',
  },
];

export function FeatureGrid({ features }: { readonly features: readonly FeaturePreview[] }): JSX.Element {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {features.map((feature) => (
        <Card key={feature.title}>
          <CardContent className="flex items-start gap-3 p-4">
            <span
              className="flex size-8 shrink-0 items-center justify-center rounded-md bg-secondary text-primary"
              aria-hidden="true"
            >
              <feature.icon className="size-4" />
            </span>
            <span className="flex flex-col gap-0.5">
              <span className="text-sm font-medium">{feature.title}</span>
              <span className="text-xs leading-relaxed text-muted-foreground">
                {feature.description}
              </span>
            </span>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

